import {isFilled} from '@prismicio/client'
import {useRouter} from 'next/router'
import type {CustomerStoryDocument, IndustryDocument} from 'prismicio-types'
import {useEffect, useMemo, useState} from 'react'
import type {Locales} from 'src/common/constants'
import {CATEGORIES} from 'src/common/constants'

const getIndustryName = (story: CustomerStoryDocument): string | undefined => {
  const industry = story.data.industry

  if (isFilled.contentRelationship(industry)) {
    return (industry.data as IndustryDocument['data'])?.name ?? undefined
  }

  return undefined
}

export const useFilteredCustomerStories = (activeCategory: number, customerStories: CustomerStoryDocument[]) => {
  const {locale} = useRouter()
  const [selectedStories, setSelectedStories] = useState(customerStories)

  const availableCategories = useMemo(() => {
    if (customerStories.length > 0) {
      const industries = Array.from(
        new Set(customerStories.map((story) => getIndustryName(story)).filter((name): name is string => !!name)),
      )

      const allCategory = CATEGORIES['All'][locale! as Locales]

      return [allCategory, ...industries]
    }

    return []
  }, [customerStories, locale])

  // Filter stories when active category or available categories change
  useEffect(() => {
    if (availableCategories.length > 0 && activeCategory !== null) {
      if (activeCategory === 0) {
        setSelectedStories(customerStories)
      } else {
        const filteredStories = customerStories.filter((story) => {
          const industryName = getIndustryName(story)
          return industryName?.toLowerCase() === availableCategories[activeCategory]?.toLowerCase()
        })

        setSelectedStories(filteredStories)
      }
    } else {
      setSelectedStories(customerStories)
    }
  }, [activeCategory, customerStories, availableCategories])

  return {selectedStories, availableCategories}
}
